import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { environment } from '@environment';
import {
  CandidateProcedure,
  CandidateProcedureDecision,
  CandidateVerification,
  UpdateCandidateVerificationDecisionOpts,
  CandidateVerificationHistory,
  NewCandidateVerificationOpts,
  PaginatedResponse,
  RequestCandidateVerificationSupplementaryInfoOpts,
  ROLES_KEYS,
  SaveCandidateVerificationResultOpts,
  SearchCandidateVerificationsOpts,
  ServiceTypeOptional,
  SmsSendResult,
  UpdateCandidateVerificationDetailsOpts,
  UpdateCandidateVerificationStatusOpts,
} from '@interfaces';
import { UserAuthFacade } from '@state/user-auth';

@Injectable({
  providedIn: 'root',
})
export class CVMHttpService {
  private readonly http = inject(HttpClient);
  private readonly userAuthFacade = inject(UserAuthFacade);

  private readonly API_URL = environment.API_URL;
  // Consultants hit the management controller; managers (employers) hit their own candidate-verification API.
  private readonly MANAGEMENT_URL = `${this.API_URL}/candidate-verification-management`;
  private readonly EMPLOYER_URL = `${this.API_URL}/candidate-verification`;

  private baseUrl$(): Observable<string> {
    return this.userAuthFacade.myself$.pipe(
      take(1),
      map(myself => (myself?.role?.key === ROLES_KEYS.Employer ? this.EMPLOYER_URL : this.MANAGEMENT_URL))
    );
  }

  public searchCandidateVerifications(
    opts: SearchCandidateVerificationsOpts
  ): Observable<PaginatedResponse<Partial<CandidateVerification>>> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl => this.http.post<PaginatedResponse<Partial<CandidateVerification>>>(`${baseUrl}/search`, opts))
    );
  }

  public getCandidateVerificationDetails(candidateVerificationId: string): Observable<CandidateVerification> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl => this.http.get<CandidateVerification>(`${baseUrl}/${candidateVerificationId}/details`))
    );
  }

  public getCandidateVerificationHistory(candidateVerificationId: string): Observable<CandidateVerificationHistory[]> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.get<CandidateVerificationHistory[]>(`${baseUrl}/${candidateVerificationId}/history`)
      )
    );
  }

  public newCandidateVerification(
    opts: NewCandidateVerificationOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.post<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>(
          `${baseUrl}/new`,
          opts
        )
      )
    );
  }

  public updateCandidateVerificationDetails(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDetailsOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult?: SmsSendResult | null }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<{ candidateVerification: Partial<CandidateVerification>; smsResult?: SmsSendResult | null }>(
          `${baseUrl}/${candidateVerificationId}/update-basic-details`,
          opts
        )
      )
    );
  }

  public updateCandidateVerificationStatus(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationStatusOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification> }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<{ candidateVerification: Partial<CandidateVerification> }>(
          `${baseUrl}/${candidateVerificationId}/status`,
          opts
        )
      )
    );
  }

  public regenerateFormLinkAndPin(candidateVerificationId: string): Observable<{ smsResult: SmsSendResult }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.get<{ smsResult: SmsSendResult }>(`${baseUrl}/${candidateVerificationId}/regenerate-pin-and-link`)
      )
    );
  }

  public extendCandidateVerificationFormValidity(opts: {
    candidateVerificationId: string;
    extendByDays: number;
  }): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>(
          `${baseUrl}/${opts.candidateVerificationId}/extend-form-validity`,
          { extendByDays: opts.extendByDays }
        )
      )
    );
  }

  public deleteCandidateVerification(
    candidateVerificationId: string
  ): Observable<{ deletedCandidateVerificationId: string }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.delete<{ deletedCandidateVerificationId: string }>(`${baseUrl}/${candidateVerificationId}`)
      )
    );
  }

  public saveCandidateVerificationResult(
    candidateVerificationId: string,
    opts: SaveCandidateVerificationResultOpts
  ): Observable<void> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl => this.http.patch<void>(`${baseUrl}/${candidateVerificationId}/result`, opts))
    );
  }

  public setCandidateVerificationDecision(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDecisionOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification> }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<{ candidateVerification: Partial<CandidateVerification> }>(
          `${baseUrl}/${candidateVerificationId}/decision`,
          opts
        )
      )
    );
  }

  public updateCandidateVerificationProcedureDecision(
    candidateVerificationId: string,
    procedureId: string,
    decision: CandidateProcedureDecision
  ): Observable<CandidateProcedure> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<CandidateProcedure>(
          `${baseUrl}/${candidateVerificationId}/procedures/${procedureId}/decision`,
          { decision }
        )
      )
    );
  }

  public addCandidateVerificationExtraService(
    candidateVerificationId: string,
    serviceType: ServiceTypeOptional
  ): Observable<CandidateProcedure> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.post<CandidateProcedure>(`${baseUrl}/${candidateVerificationId}/procedures/extra-services`, {
          type: serviceType,
        })
      )
    );
  }

  public downloadCandidateFile(candidateVerificationId: string, fileId: string): Observable<HttpResponse<Blob>> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.get(`${baseUrl}/${candidateVerificationId}/files/${fileId}`, {
          responseType: 'blob',
          observe: 'response',
        })
      )
    );
  }

  public requestSuplementaryInfo(
    candidateVerificationId: string,
    opts: RequestCandidateVerificationSupplementaryInfoOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }> {
    return this.baseUrl$().pipe(
      switchMap(baseUrl =>
        this.http.patch<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>(
          `${baseUrl}/${candidateVerificationId}/request-suplementary-info`,
          opts
        )
      )
    );
  }
}
